/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-30 16:41:43
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-23 15:22:00
 * @ Description: Dialog for changing the preferences
 */

import React, { useState, useEffect } from 'react'
// Centralized lodash imports - much cleaner!
import {
  map,
  merge,
  includes,
  filter,
  concat,
  forEach,
  cloneDeep,
  isArray,
  isNil,
  set,
  unset,
  head,
  sortBy,
  findIndex,
  slice,
  isEmpty,
  isEqual,
  pullAt,
  intersection,
} from '@/utils/lodash'
import { useTheme } from '@mui/material/styles'
import { styled } from '@mui/system'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconWrapper } from '@/housefolios-components/shared'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  arrayMove,
  SortableItem,
  SortableList,
} from '@/components/sortable'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import { useMutation } from '@apollo/client/react'
import { UPDATE_ORGANIZATION } from '../mutations'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone'
import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MenuIcon from '@mui/icons-material/Menu'
import BuyBox from '@/housefolios-components/Dialogs/BuyBox'
import { propertyClasses } from '@/housefolios-components/Comps/FindComps'
import useAffiliate from '@/hooks/useAffiliate'
import {
  defaultStrategies,
  filterPreferedStrategies,
  getStrategyName,
} from '@/utils/stratagies'
import usePreferences from '@/hooks/usePreferences'

const drawerWidth = 240
const navItemSx = {
  py: 1.25,
  px: 2,
  '&.Mui-selected': {
    bgcolor: 'rgba(54, 140, 255, 0.12)',
    color: 'primary.main',
    '& .MuiListItemIcon-root': { color: 'primary.main' },
  },
  '&.Mui-selected:hover': {
    bgcolor: 'rgba(54, 140, 255, 0.18)',
  },
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
})

const AppBarStyled = styled(AppBar)({})

const MenuButton = styled(IconButton)(({ theme  }) => ({
  marginRight: theme.spacing(2),
}))

const Content = styled('main')(({ theme  }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  minWidth: 0,
  overflow: 'auto',
}))

const DrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'drawerOpen',
})(({ drawerOpen  }) => ({
  width: drawerOpen ? drawerWidth : 0,
  flexShrink: 0,
  overflowX: drawerOpen ? 'visible' : 'hidden',
  '& .MuiDrawer-paper': {
    position: 'relative',
    width: drawerWidth,
    boxSizing: 'border-box',
    border: 0,
    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  },
}))

const DrawerHeader = styled('div')(({ theme  }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  minHeight: 56,
  justifyContent: 'flex-end',
}))

const SortableHelper = styled('div')({
  zIndex: 5000,
})

export const DEFAULT_PREFERENCES = {
  rent: [
    // 'houseCanaryHigh',
    // 'houseCanaryAverage',
    // 'houseCanaryLow',
    'housefoliosAssumption',
  ], //'rentometerHigh', 'rentometer', 'rentometerLow'],
  // avgRate: ['airdna'],
  // occupancyRate: ['airdna'],
  afterRepairValue: [
    // 'houseCanaryHigh',
    'averageCompValues',
    // 'houseCanaryAverage',
    // 'houseCanaryLow',
  ],
  listPrice: ['house', 'housefoliosAssumption'],
  propertyTaxes: [
    // 'houseCanary',
    'smartAsset',
    'housefoliosAssumption',
  ],
  rehabCost: ['housefoliosAssumption'],
  offerPrice: ['housefoliosAssumption'],
  buyAndHoldOrder: [
    'strategy',
    'targets',
    'monthlyCashflow',
    'acquisition',
    'income',
    'expenses',
    'rehab',
    'financing',
    'projection',
    'cashPositioning',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  fixAndFlipOrder: [
    'bigPicture',
    'bigPictureSummary',
    'strategy',
    'targets',
    'netProfit',
    'acquisition',
    'income',
    'rehab',
    'holding',
    'financing',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  turnkeyOrder: [
    'strategy',
    'targets',
    'monthlyCashflow',
    'acquisition',
    'income',
    'expenses',
    'rehab',
    'financing',
    'projection',
    'cashPositioning',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  BRRRROrder: [
    'strategy',
    'targets',
    'monthlyCashflow',
    'acquisition',
    'income',
    'expenses',
    'rehab',
    'financing',
    'projection',
    'cashPositioning',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  shortTermRentalOrder: [
    'strategy',
    'targets',
    'monthlyCashflow',
    'acquisition',
    'income',
    'expenses',
    'rehab',
    'financing',
    'projection',
    'cashPositioning',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  buildToRentOrder: [
    'strategy',
    'targets',
    'monthlyCashflow',
    'income',
    'expenses',
    'financing',
    'endFinancing',
    'projection',
    'cashPositioning',
    'disposition',
    'comparableProperties',
    'deactive',
  ],
  wholesaleOrder: [
    'bigPicture',
    'bigPictureSummary',
    'strategy',
    'targets',
    'netProfit',
    'wholesale',
    'comparableProperties',
    'deactive',
  ],
  BOROrder: [
    'strategy',
    'targets',
    'BOR',
    'BORInputs',
    'comparableProperties',
    'deactive',
  ],
  launchPad: [
    'Property Search',
    'Add Property',
    'Upload Properties',
    'Marketplace',
    'Create Portfolio',
    'Edit Assumptions',
    'Personal',
    'Organization',
    'Team',
    'My Marketplaces',
    'Billing',
    'Add Lot',
    'Tools',
  ],
  contactAgentPreferences: {
    conditions: '',
    statements: '',
    inspectionPeriod: '',
    daysToClose: '',
    earnestMoney: '',
  },
  contactAgentTemplate: {
    conditions: '',
    statements: '',
    inspectionPeriod: '',
    daysToClose: '',
    earnestMoney: '',
  },
  findComps: {
    beds: { min: -1, max: 1 },
    baths: { min: -1, max: 1 },
    sqft: { min: 0.75, max: 1.25 },
    lotSqft: { min: 0, max: 0 },
    year: { min: -10, max: 10 },
    maxDistance: 'Draw Comp Area',
    soldBy: '12',
    activeMLS: '',
    propertyType: '',
    propertyClass: '',
    compCount: 250,
  },
  general: {
    defaultPage: {
      label: 'Launchpad',
      value: '/launchpad',
    },
    defaultStrategy: 'Buy & Hold',
    strategies: [
      {
        value: 'Buy & Hold',
        active: true,
      },
      {
        value: 'Fix & Flip',
        active: true,
      },
      {
        value: 'Turnkey End Buyer',
        active: true,
      },
      {
        value: 'BRRRR',
        active: true,
      },
      {
        value: 'Short Term Rental',
        active: true,
      },
      {
        value: 'Wholesale',
        active: false,
        memberExcluded: ['astroflip'],
      },
      {
        value: 'Build to Rent',
        active: false,
      },
      {
        value: 'BOR',
        active: false,
        memberOnly: 'flippingamerica',
      },
      {
        value: 'Astroflip',
        active: false,
        memberOnly: 'astroflip',
      },
    ],
    // rehabOptions: [
    //   {
    //     value: 'singleRehabValue',
    //     label: 'Single Rehab Value',
    //     active: true,
    //   },
    //   {
    //     value: 'detailedRehab',
    //     label: 'Detailed Rehab',
    //     active: true,
    //   },
    //   {
    //     value: 'rehabTier',
    //     label: 'Rehab Tier',
    //     active: true,
    //   },
    //   {
    //     value: 'flippingAmericaRehab',
    //     label: 'Flipping America Rehab',
    //     active: false,
    //   },
    // ],
    isThisYourProperty: true,
    completePropertyData: true,
  },
}

function OrganizationPreferencesComponent(props) {
  const theme = useTheme()
  const [
    openOrganizationPreferences,
    setOpenOrganizationPreferences,
  ] = useState(false)
  const [openBuyBox, setOpenBuyBox] = useState(false)
  const handleOpenOrganizationPreferences = () => {
    setOpenOrganizationPreferences(true)
    setOpenBuyBox(false)
  }
  const handleCloseOrganizationPreferences = () => {
    setOpenOrganizationPreferences(false)
  }

  const [open, setOpen] = React.useState(true)
  const handleDrawerOpen = () => {
    setOpen((prev) => true)
  }
  const handleDrawerClose = () => {
    setOpen((prev) => false)
  }

  const [tab, setTab] = useState('General')

  const [{ affiliate, urlAffiliate }] = useAffiliate({})

  const listItems = [
    {
      name: 'General',
      icon: <SettingsTwoToneIcon />,
    },
    {
      name: 'Field Options',
      icon: (
        <FontAwesomeIcon icon={['fas', 'database']} style={{ mr: '0.5rem' }} sx={{ fontSize: "1.265rem !important" }} />
      ),
      // },{
      //   name: 'Comps',
      //   icon: <FontAwesomeIcon icon={['far','house-circle-check']} />,
    },
    {
      name: 'Strategy Page Layout',
      icon: (
        <FontAwesomeIcon icon={['far', 'object-ungroup']} style={{ mr: '0.5rem' }} sx={{ fontSize: "1.265rem !important" }} />
      ),
    },
    {
      name: 'Launchpad',
      icon: (
        <RocketLaunchIcon sx={[{ mr: '0.5rem' }, { fontSize: "1.265rem !important" }]} />
      ),
    },
  ]

  const [expanded, setExpanded] = React.useState(false)

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const [preferences, setPreferences] = useState(
    merge(
      cloneDeep(DEFAULT_PREFERENCES),
      cloneDeep(props?.session?.me?.activeOrg?.memberPreferences),
      cloneDeep(props?.session?.me?.activeOrg?.preferences),
    ),
  )

  useEffect(() => {
    let newPreferences = merge(
      cloneDeep(DEFAULT_PREFERENCES),
      cloneDeep(props?.session?.me?.activeOrg?.memberPreferences),
      cloneDeep(props?.session?.me?.activeOrg?.preferences),
    )
    forEach(newPreferences, (preference, key) => {
      let newPreference = preference
      if (isArray(newPreference)) {
        const newValues = filter(
          DEFAULT_PREFERENCES[key],
          (x) => !includes(newPreference, x),
        )
        newPreference = filter(newPreference, (x) =>
          includes(DEFAULT_PREFERENCES[key], x),
        )
        const deactiveIndex = findIndex(
          newPreference,
          (value) => value === 'deactive',
        )
        if (deactiveIndex >= 0)
          newPreference = concat(
            slice(newPreference, 0, deactiveIndex),
            newValues,
            slice(newPreference, deactiveIndex),
          )
        else newPreference = concat(newPreference, newValues)
      }
      newPreferences[key] = newPreference
    })
    if (
      !includes(
        filterPreferedStrategies(
          preferences,
          props?.session?.me?.activeOrg?.member,
          true,
        ),
        newPreferences.general['defaultStrategy'],
      )
    )
      set(
        newPreferences,
        'general.defaultStrategy',
        head(
          filterPreferedStrategies(
            preferences,
            props?.session?.me?.activeOrg?.member,
            true,
          ),
        ),
      )
    if (
      !isEqual(
        newPreferences,
        props?.session?.me?.activeOrg?.preferences,
      )
    )
      setPreferences(newPreferences)
  }, [props?.session?.me?.activeOrg?.preferences])

  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION)

  const [openReset, setOpenReset] = useState(false)
  const [resetAll, setResetAll] = useState('all')

  const defaultPages = filter(
    [
      {
        label: 'Launchpad',
        value: '/launchpad',
      },
      {
        label: 'Property Search',
        value: '/property-search',
        hide:
          String(props?.session?.me?.activeOrg?.member || '').toLowerCase() ===
          'pep',
      },
      {
        label: 'My Properties',
        value: '/my-properties',
      },
      {
        label: 'Housefolios Marketplace',
        value: '/marketplace',
        hide: urlAffiliate,
      },
    ],
    (option) => !option.hide,
  )

  const menuTitle = [
    {
      name: 'listPrice',
      label: 'Asking Price',
    },
    {
      name: 'offerPrice',
      label: 'Purchase Price',
    },
    {
      name: 'afterRepairValue',
      label: 'After Repair Value',
    },
    {
      name: 'rent',
      label: 'Total Rental Income',
    },
    {
      name: 'propertyTaxes',
      label: 'Annual Taxes',
    },
    {
      name: 'rehabCost',
      label: 'Estimated Rehab',
    },
    // {
    //   name: 'avgRate',
    //   label: 'Average Rate',
    // },
    // {
    //   name: 'occupancyRate',
    //   label: 'Occupancy Rate',
    // },
  ]

  const compFields = [
    {
      name: 'beds',
      label: 'Beds Ranges',
      min: -5,
      max: 5,
    },
    {
      name: 'baths',
      label: 'Baths Ranges',
      min: -5,
      max: 5,
    },
    {
      name: 'sqft',
      label: 'SqFt Ranges',
      min: 0,
      max: 2,
      step: 0.25,
      valueLabelFormat: (x) => x * 100 + '%',
    },
    // {
    //   name: 'lotSqft',
    //   label: 'Lot SqFt Ranges',
    //   min: 0,
    //   max: 2,
    //   step: 0.25,
    //   valueLabelFormat: x => (x*100) + '%',
    // },
    {
      name: 'year',
      label: 'Year Built Ranges',
      min: -50,
      max: 50,
    },
    {
      label: 'Max Distance (miles)*',
      placeholder: 'Max Distance (miles)*',
      name: 'maxDistance',
      suffix: 'miles',
      options: [
        'Draw Comp Area',
        '0.25',
        '.5',
        '.75',
        '1',
        '1.5',
        '2',
        '2.5',
        '3',
        '4',
        '5',
      ],
      width: '6',
    },
    {
      label: 'Sold Within (months)*',
      placeholder: 'Sold Within (months)*',
      name: 'soldBy',
      suffix: 'months',
      options: ['3', '6', '12', '18', '24', '36', '48', '60'],
      width: '6',
    },
    // {
    //   label: 'Market',
    //   placeholder: 'Both',
    //   name: 'activeMLS',
    //   options: ['Active MLS', 'Off Market', 'Both'],
    //   width: '6'
    //  },
    {
      label: 'Property Type',
      placeholder: 'Property Type',
      name: 'propertyType',
      options: [
        'ANY',
        'SFR',
        'MFR',
        'LAND',
        'CONDO',
        'MOBILE',
        'OTHER',
      ],
      width: '6',
    },
    {
      label: 'Property Class',
      multiple: true,
      placeholder: 'Property Class',
      name: 'propertyClass',
      options: propertyClasses,
      width: '6',
    },
  ]

  const compParams = {
    beds: {
      min:
        4 + preferences.findComps.beds.min > 0
          ? 4 + preferences.findComps.beds.min
          : 0,
      max:
        4 + preferences.findComps.beds.max > 0
          ? 4 + preferences.findComps.beds.max
          : 0,
    },
    baths: {
      min:
        2 + preferences.findComps.baths.min > 0
          ? 2 + preferences.findComps.baths.min
          : 0,
      max:
        2 + preferences.findComps.baths.max > 0
          ? 2 + preferences.findComps.baths.max
          : 0,
    },
    sqft: {
      min:
        2000 * preferences.findComps.sqft.min > 0
          ? 2000 * preferences.findComps.sqft.min
          : 0,
      max:
        2000 * preferences.findComps.sqft.max > 0
          ? 2000 * preferences.findComps.sqft.max
          : 0,
    },
    year: {
      min:
        1965 + preferences.findComps.year.min > 0
          ? 1965 + preferences.findComps.year.min
          : 0,
      max:
        1965 + preferences.findComps.year.max > 0
          ? 1965 + preferences.findComps.year.max
          : 0,
    },
  }

  const soldBy = preferences.findComps.soldBy
  const maxDistance = preferences.findComps.maxDistance

  const propertyDetailsFields = [
    {
      label: 'Beds Min',
      placeholder: 'Beds Min',
      name: 'beds.min',
      value: compParams.beds.min,
      width: '6',
    },
    {
      label: 'Beds Max',
      placeholder: 'Beds Min',
      name: 'beds.max',
      value: compParams.beds.max,
      required: true,
      width: '6',
    },
    {
      label: 'Baths Min',
      placeholder: 'Baths Min',
      name: 'baths.min',
      value: compParams.baths.min,
      width: '6',
    },
    {
      label: 'Baths Max',
      placeholder: 'Baths Min',
      name: 'baths.max',
      value: compParams.baths.max,
      required: true,
      width: '6',
    },
    {
      label: 'Sqft Min',
      placeholder: 'Sqft Min',
      name: 'sqft.min',
      value: compParams.sqft.min,
      width: '6',
    },
    {
      label: 'Sqft Max',
      placeholder: 'Sqft Min',
      name: 'sqft.max',
      value: compParams.sqft.max,
      required: true,
      width: '6',
    },
    {
      label: 'Year Min',
      placeholder: 'Year Min',
      name: 'year.min',
      value: compParams.year.min,
      width: '6',
    },
    {
      label: 'Year Max',
      placeholder: 'Year Max',
      name: 'year.max',
      value: compParams.year.max,
      required: true,
      width: '6',
    },
  ]

  const otherFields = [
    {
      label: 'Max Distance (miles)*',
      placeholder: 'Max Distance (miles)*',
      value: maxDistance,
      suffix: 'miles',
      options: [
        '0.25',
        '.5',
        '.75',
        '1',
        '1.5',
        '2',
        '2.5',
        '3',
        '4',
        '5',
      ],
      width: '6',
    },
    {
      label: 'Sold Within (months)*',
      placeholder: 'Sold Within (months)*',
      value: soldBy,
      suffix: 'months',
      options: ['3', '6', '12', '18', '24'],
      width: '6',
    },
  ]

  const listDescriptions = {
    house: 'Provided by House',
    housefolios: 'Based on Approx. Assessed Value',
    housefoliosCounty: 'Estimate based on County',
    housefoliosGreatest:
      'Estimate based on the greatest of Sqft/Beds',
    housefoliosSqft: 'Estimate based on Sqft',
    housefoliosAssumption: 'Based on Assumptions',
    housefoliosBed: 'Estimate based on Bedrooms',
    housefoliosBedAndSqft: 'Estimate based on Sqft & Beds',
    smartAsset: 'Provided by Smart Asset',
    // rentometerHigh: 'Provided by Rentometer - High value',
    // rentometer: 'Provided by Rentometer - Mid value',
    // rentometerLow: 'Provided by Rentometer- Low value',
    listPriceEstimate: 'Estimated From Asking Price',
    // housefoliosAverage: 'Average value from Housefoliios',
    // housefoliosHigh: 'High value from Housefoliios',
    // housefoliosLow: 'Low value from Housefoliios',
    // reAPI: 'Estimated AVM Provided by REAPI',
    // houseCanary: 'Provided by HouseCanary',
    // houseCanaryAverage: 'Provided by HouseCanary - Mid value',
    // houseCanaryHigh: 'Provided by HouseCanary - High value',
    // houseCanaryLow: 'Provided by HouseCanary - Low value',
    // airdna: 'Provided by AirDNA',
    averageCompValues: 'Adjusted Comp Average',
    averageLowestCompValues: 'Adjusted Lowest 3 Comp Average',
  }

  const [
    {
      buyAndHoldCards,
      fixAndFlipCards,
      turnkeyCards,
      BRRRRCards,
      shortTermRentalCards,
      buildToRentCards,
      wholesaleCards,
      BORCards,
      astroflipCards,
    },
    // { getOrderDefault },
  ] = usePreferences({ activeOrg: props.session.me.activeOrg  })

  const menuTitleStategy = filter(
    [
      {
        name: 'buyAndHoldOrder',
        label: 'Buy & Hold',
        cards: buyAndHoldCards,
      },
      {
        name: 'fixAndFlipOrder',
        label: 'Fix & Flip',
        cards: fixAndFlipCards,
      },
      {
        name: 'turnkeyOrder',
        label: 'Turnkey End Buyer',
        cards: turnkeyCards,
      },
      {
        name: 'BRRRROrder',
        label: 'BRRRR',
        cards: BRRRRCards,
      },
      {
        name: 'shortTermRentalOrder',
        label: 'Short Term Rental',
        cards: shortTermRentalCards,
      },
      {
        name: 'buildToRentOrder',
        label: 'Build to Rent',
        cards: buildToRentCards,
      },
      {
        name: 'wholesaleOrder',
        label: 'Wholesale',
        cards: wholesaleCards,
        memberExcluded: ['astroflip'],
      },
      {
        name: 'BOROrder',
        label: 'BOR',
        cards: BORCards,
        memberOnly: 'flippingamerica',
      },
      {
        name: 'astroflipOrder',
        label: 'Astroflip',
        cards: astroflipCards,
        memberOnly: 'astroflip',
      },
    ],
    (strategy) => {
      return (
        (!strategy.memberOnly ||
          strategy.memberOnly ===
            props?.session?.me?.activeOrg?.member) &&
        (!strategy.memberExcluded ||
          !includes(
            strategy.memberExcluded,
            props?.session?.me?.activeOrg?.member,
          ))
      )
    },
  )

  let launchPadButtons = [
    {
      title: 'Property Search',
      subtitle: 'Search active listings',
      icon: <IconWrapper icon={['fas', 'magnifying-glass']} />,
    },
    {
      title: 'Add Property',
      subtitle: 'Listed or off-market',
      icon: <IconWrapper icon={['fas', 'location-dot']} />,
    },
    {
      title: 'Upload Properties',
      subtitle: 'Upload spreadsheet',
      icon: <IconWrapper icon={['far', 'file-excel']} />,
    },
    {
      title: 'Marketplace',
      subtitle: 'Pre-analyzed deals',
      icon: <IconWrapper icon={['fas', 'sign-hanging']} />,
    },
    {
      title: 'Create Portfolio',
      subtitle: 'Keep it organiized',
      icon: <IconWrapper icon={['fas', 'folder-plus']} />,
    },
    {
      title: 'Edit Assumptions',
      subtitle: 'Set assumptions & targets',
      icon: <IconWrapper icon={['fas', 'sliders']} />,
    },
    {
      title: 'Personal',
      subtitle: 'Personal Information',
      icon: <IconWrapper icon={['far', 'user']} />,
    },
    {
      title: 'Organization',
      subtitle: 'Organization Information',
      icon: <IconWrapper icon={['far', 'building']} />,
    },
    {
      title: 'Team',
      subtitle: 'List of Team Members',
      icon: <IconWrapper icon={['fas', 'user-group']} />,
    },
    {
      title: 'My Marketplaces',
      subtitle: 'Your Marketplaces',
      icon: <IconWrapper icon={['fas', 'sign-hanging']} />,
    },
    {
      title: 'Billing',
      subtitle: 'Plan and Billing details',
      icon: <IconWrapper icon={['far', 'file-lines']} />,
    },
    {
      title: 'Add Lot',
      subtitle: 'Listed or off-market',
      icon: <IconWrapper icon={['fal', 'map-marked-alt']} />,
    },
    {
      title: 'Tools',
      subtitle: 'Access various tools',
      icon: <IconWrapper icon={['fas', 'hammer']} />,
    },
  ]

  if (!props.session.me.activeOrg.manageLots)
    pullAt(launchPadButtons, 11)

  const sortedLaunchPad = sortBy(launchPadButtons, [
    (o) => {
      const idx = findIndex(
        preferences['launchPad'],
        (value) => value === o.title,
      )
      return idx === -1 ? 9999 : idx
    },
  ])
  const launchPadExtra = 3 - (sortedLaunchPad.length % 3)

  const onSortEnd =
    (items, collection) =>
    ({ oldIndex, newIndex  }) => {
      let newPreferences = { ...preferences }
      newPreferences[collection] = intersection(
        newPreferences[collection],
        items,
      )
      newPreferences[collection] = arrayMove(
        newPreferences[collection],
        oldIndex,
        newIndex,
      )
      setPreferences(newPreferences)
      updateOrganization({
        variables: {
          id: props.session.me.activeOrg._id,
          organizationInput: {
            preferences: newPreferences,
          },
        },
        refetchQueries: [{ query: GET_ME }],
      })
    }

  const handleChangeSlider = (
    event,
    value,
    collection,
    name,
    commit = false,
  ) => {
    let newPreferences = { ...preferences }
    newPreferences[collection][name] = {
      min: value[0],
      max: value[1],
    }
    setPreferences(newPreferences)
    if (commit) {
      updateOrganization({
        variables: {
          id: props.session.me.activeOrg._id,
          organizationInput: {
            preferences: newPreferences,
          },
        },
        refetchQueries: [{ query: GET_ME }],
      })
    }
  }

  const handleChangeDropdown = (event, value, collection, name) => {
    let newPreferences = { ...preferences }
    let newValue = value
    if (name === 'strategies') {
      newPreferences[collection][name] = map(
        newPreferences[collection][name],
        (preference) => {
          return {
            value: preference.value,
            active: includes(newValue, preference.value),
            memberOnly: preference.memberOnly,
            memberExcluded: preference.memberExcluded,
          }
        },
      )
    } else newPreferences[collection][name] = newValue
    setPreferences(newPreferences)
    updateOrganization({
      variables: {
        id: props.session.me.activeOrg._id,
        organizationInput: {
          preferences: newPreferences,
        },
      },
      refetchQueries: [{ query: GET_ME }],
    })
  }

  const handleReset = () => {
    let newPreferences = merge(
      cloneDeep(DEFAULT_PREFERENCES),
      cloneDeep(props?.session?.me?.activeOrg?.memberPreferences),
    )
    if (resetAll !== 'all') {
      if (tab === 'Comps') {
        newPreferences = { findComps: newPreferences.findComps }
      } else if (tab === 'Strategy Page Layout') {
        newPreferences = {
          buyAndHoldOrder: newPreferences.buyAndHoldOrder,
          fixAndFlipOrder: newPreferences.fixAndFlipOrder,
          turnkeyOrder: newPreferences.turnkeyOrder,
          BRRRROrder: newPreferences.BRRRROrder,
          shortTermRentalOrder: newPreferences.shortTermRentalOrder,
          buildToRentOrder: newPreferences.buildToRentOrder,
          wholesaleOrder: newPreferences.wholesaleOrder,
          BOROrder: newPreferences.BOROrder,
        }
      } else if (tab === 'Launchpad') {
        newPreferences = {
          launchPad: newPreferences.launchPad,
        }
      } else if (tab === 'General') {
        newPreferences = {
          general: newPreferences.general,
        }
      } else {
        unset(newPreferences, 'findComps')
        unset(newPreferences, 'buyAndHoldOrder')
        unset(newPreferences, 'fixAndFlipOrder')
        unset(newPreferences, 'turnkeyOrder')
        unset(newPreferences, 'BRRRROrder')
        unset(newPreferences, 'shortTermRentalOrder')
        unset(newPreferences, 'buildToRentOrder')
        unset(newPreferences, 'wholesaleOrder')
        unset(newPreferences, 'BOROrder')
        unset(newPreferences, 'fieldOptions')
        unset(newPreferences, 'general')
      }
    }
    newPreferences = merge(cloneDeep(preferences), newPreferences)
    setPreferences(newPreferences)
    updateOrganization({
      variables: {
        id: props.session.me.activeOrg._id,
        organizationInput: {
          preferences: newPreferences,
        },
      },
      refetchQueries: [{ query: GET_ME }],
    })
      .then(() => {
        enqueueSnackbar(`Preferences Successfully Reset`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        setOpenReset(false)
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to Reset Preferences`, {
          variant: 'error',
          autoHideDuration: 3000,
        })
        setOpenReset(false)
      })
  }

  return (
    <Box>
      <MenuItem onClick={handleOpenOrganizationPreferences}>
        <FontAwesomeIcon icon={['fas', 'asterisk']} style={{ marginRight: 10, width: '1rem' }} sx={{ fontSize: "1.15rem !important" }} />
        <Typography>Organization Preferences</Typography>
      </MenuItem>

      {/* <Button variant='outlined' color='primary' size='small' fullWidth={true} onClick={handleOpenOrganizationPreferences}>
         <Typography>Organization Preferences</Typography>
       </Button> */}
      <Dialog
        open={openOrganizationPreferences}
        onClose={handleCloseOrganizationPreferences}
        fullScreen
        slotProps={{
          paper: {
            sx: { borderRadius: 0, height: '100%', maxHeight: '100%' },
          },
        }}
      >
        <Root>
          <CssBaseline />
          <AppBarStyled position="relative">
            <Toolbar>
              <MenuButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                size="large"
                sx={{
                  color: 'white',
                  ...(open && { display: 'none'  }),
                }}
              >
                <MenuIcon />
              </MenuButton>
              <Typography
                variant="h6"
                style={{ color: 'white' }}
                noWrap
              >
                Organization Preferences
              </Typography>
              <Typography style={{ flex: 1 }} />
              <Button
                variant="outlined"
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  marginRight: 5,
                }}
                onClick={() => setOpenReset(true)}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
                onClick={handleCloseOrganizationPreferences}
              >
                Close
              </Button>
            </Toolbar>
          </AppBarStyled>
          <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <DrawerStyled
            variant="persistent"
            anchor="left"
            open={open}
            drawerOpen={open}
          >
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose} size="large">
                {theme.direction === 'ltr' ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List>
              {map(listItems, (section) => (
                <ListItemButton
                  key={section.name}
                  selected={tab === section.name}
                  onClick={() => setTab(section.name)}
                  sx={navItemSx}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {section.icon}
                  </ListItemIcon>
                  <ListItemText primary={section.name} />
                </ListItemButton>
              ))}
              {!urlAffiliate && (
                <ListItemButton
                  sx={navItemSx}
                  onClick={() => {
                    handleCloseOrganizationPreferences()
                    setOpenBuyBox(true)
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FontAwesomeIcon icon={['fas', 'crosshairs']} sx={{ fontSize: "1.265rem !important" }} />
                  </ListItemIcon>
                  <ListItemText primary="Buy Box" />
                </ListItemButton>
              )}
            </List>
          </DrawerStyled>
            <Content>
              {tab === 'General' && (
                <Grid container>
                  <Grid size={12}>
                    <Typography
                      variant="h5"
                      style={{ paddingTop: 25 }}
                    >
                      General
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 25 }}
                  >
                    <Typography variant="subtitle1">
                      A list of general preferences for your{' '}
                      {affiliate?.companyName || 'Housefolios'}{' '}
                      account
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="h6">Home Page</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      Choose which page you want to go to when you log
                      in to {affiliate?.companyName || 'Housefolios'}.
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <Autocomplete
                      id="checkboxes-tags-demo"
                      options={defaultPages}
                      value={preferences.general['defaultPage']}
                      onChange={(event, newValue) => {
                        handleChangeDropdown(
                          event,
                          newValue,
                          'general',
                          'defaultPage',
                        )
                      }}
                      disableClearable
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Default Page"
                          placeholder="Default Page"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="h6">Strategy</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      Choose which strategies you want displayed on
                      your Property Dashboard.
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <Autocomplete
                      id="checkboxes-tags-demo"
                      multiple
                      disableCloseOnSelect
                      options={filterPreferedStrategies(
                        preferences,
                        props?.session?.me?.activeOrg?.member,
                        false,
                      )}
                      value={filterPreferedStrategies(
                        preferences,
                        props?.session?.me?.activeOrg?.member,
                        true,
                      )}
                      getOptionLabel={(option) =>
                        getStrategyName(
                          option,
                          props?.session?.me?.activeOrg?.member,
                        )
                      }
                      onChange={(event, newValue) => {
                        if (!isEmpty(newValue))
                          handleChangeDropdown(
                            event,
                            newValue,
                            'general',
                            'strategies',
                          )
                      }}
                      disableClearable
                      // isOptionEqualToValue={(option, value) =>
                      //   option === value.value && value.active
                      // }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Strategies"
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="h6">
                      Default Strategy
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      Choose which is the default strategy used for
                      portfolios.
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <Autocomplete
                      id="checkboxes-tags-demo"
                      options={filterPreferedStrategies(
                        preferences,
                        props?.session?.me?.activeOrg?.member,
                        true,
                      )}
                      getOptionLabel={(option) =>
                        getStrategyName(
                          option,
                          props?.session?.me?.activeOrg?.member,
                        )
                      }
                      value={preferences.general['defaultStrategy']}
                      onChange={(event, newValue) => {
                        if (!isEmpty(newValue))
                          handleChangeDropdown(
                            event,
                            newValue,
                            'general',
                            'defaultStrategy',
                          )
                      }}
                      disableClearable
                      isOptionEqualToValue={(option, value) =>
                        option === value.value && value.active
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Strategies"
                        />
                      )}
                    />
                  </Grid>
                  {/* <Grid size={12}>
                    <Typography variant="h6">Rehab Option</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      Choose which rehab options you want avaliable on the Property Dashboard.
                    </Typography>
                  </Grid>
                  <Grid size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <Autocomplete
                      id="checkboxes-tags-demo"
                      multiple
                      disableCloseOnSelect
                      options={map(get(preferences, 'general.rehabOptions'), option => option.label)}
                      value={map(filter(get(preferences, 'general.rehabOptions'), options => options.active), option => option.label)}
                      // getOptionLabel={option => getStrategyName(option, props?.session?.me?.activeOrg?.member)}
                      onChange={(event, newValue) => {
                        if (!isEmpty(newValue))
                          handleChangeDropdown(
                            event,
                            newValue,
                            'general',
                            'rehabOptions',
                          )
                      }}
                      disableClearable
                      // isOptionEqualToValue={(option, value) =>
                      //   option === value.value && value.active
                      // }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Rehab Options"
                        />
                      )}
                    />
                  </Grid> */}
                  <Grid size={12}>
                    <Typography variant="h6">Add Property</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2">
                      Toggle on/off the Complete Property Data window
                      while Adding Properties.
                    </Typography>
                    {/* <Typography variant='subtitle2'>Toggle on/off the Is This Your Property and Complete Property Data window while Adding Properties.</Typography> */}
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <TextField
                      variant="outlined"
                      margin="dense"
                      select
                      value={
                        preferences.general['isThisYourProperty']
                      }
                      onChange={(event) =>
                        handleChangeDropdown(
                          event,
                          event.target.value,
                          'general',
                          'isThisYourProperty',
                        )
                      }
                      fullWidth={true}
                      label="Is This Your Property"
                    >
                      <MenuItem key="on" value={true}>
                        On
                      </MenuItem>
                      <MenuItem key="off" value={false}>
                        Off
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <TextField
                      variant="outlined"
                      margin="dense"
                      select
                      value={
                        preferences.general['completePropertyData']
                      }
                      onChange={(event) =>
                        handleChangeDropdown(
                          event,
                          event.target.value,
                          'general',
                          'completePropertyData',
                        )
                      }
                      fullWidth={true}
                      label="Complete Property Data"
                    >
                      <MenuItem key="on" value={true}>
                        On
                      </MenuItem>
                      <MenuItem key="off" value={false}>
                        Off
                      </MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              )}
              {tab === 'Field Options' && (
                <Grid container>
                  <Grid size={12}>
                    <Typography
                      variant="h5"
                      style={{ paddingTop: 25 }}
                    >
                      Field Options
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 25 }}
                  >
                    <Typography variant="subtitle1">
                      Change your data source preferences. Rearrange
                      the order by dragging them. The top is your most
                      preferred.
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <div
                    //
                    >
                      {map(menuTitle, (title) => (
                        <Accordion
                          key={title.name}
                          expanded={expanded === title.name}
                          onChange={handleChange(title.name)}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1bh-content"
                            id="panel1bh-header"
                          >
                            <Typography
                            //
                            >
                              {title.label}
                            </Typography>
                          </AccordionSummary>
                          <Divider />
                          <AccordionDetails>
                            <SortableHelper>
                              <SortableList
                                items={(
                                  preferences[title.name] || []
                                ).map(String)}
                                onSortEnd={onSortEnd(
                                  preferences[title.name],
                                  title.name,
                                )}
                              >
                                <Grid container spacing={1}>
                                  {(
                                    preferences[title.name] || []
                                  ).map((value, index) => (
                                    <Grid
                                      size={12}
                                      key={`${title.name}-${value}-${index}`}
                                    >
                                      <SortableItem
                                        id={String(value)}
                                      >
                                        <Card>
                                          <CardContent>
                                            <Grid
                                              container
                                              spacing={2}
                                              sx={{
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Grid>
                                                <FontAwesomeIcon icon={[ 'fas', 'grip-vertical', ]} style={{ color: '#797d95', }} sx={{ fontSize: "1.265rem !important" }} />
                                              </Grid>
                                              <Grid>
                                                <Typography>
                                                  {listDescriptions[
                                                    value
                                                  ]?.replace(
                                                    'Provided by House',
                                                    'Provided by ' +
                                                      (affiliate
                                                        ? affiliate?.companyName ||
                                                          'House'
                                                        : 'House'),
                                                  )}
                                                </Typography>
                                              </Grid>
                                            </Grid>
                                          </CardContent>
                                        </Card>
                                      </SortableItem>
                                    </Grid>
                                  ))}
                                </Grid>
                              </SortableList>
                            </SortableHelper>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </div>
                  </Grid>
                </Grid>
              )}
              {tab === 'Comps' && (
                <Grid container direction="column">
                  <Grid size={12}>
                    <Typography
                      variant="h5"
                      style={{ paddingTop: 25 }}
                    >
                      Comps
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 50 }}
                    sx={{
                      justifyContent: 'center',
                    }}
                  >
                    {/* <Typography variant='h6'>Change your default values for finding comps. Preferences are in comparasion to the property values.</Typography> */}
                    <Typography variant="subtitle1">
                      Adjusting these ranges helps Housefolios know
                      what you consider a Comp property. They also
                      change the values in the Example section which
                      reflect a Comp search for a Property with 4
                      Beds, 2 Baths, 2000 Sqft and built in 1965
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Grid
                      container
                      direction="row"
                      spacing={5}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                      }}
                    >
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <div
                        //
                        >
                          <Grid
                            container
                            direction="column"
                            spacing={1}
                          >
                            {map(compFields, (field) => {
                              if (
                                !isNil(
                                  preferences.findComps[field.name]
                                    .min,
                                )
                              ) {
                                return (
                                  <Grid size={12}>
                                    <Typography
                                      style={{ marginBottom: -25 }}
                                    >
                                      {field.label}
                                    </Typography>
                                    <Slider
                                      track="inverted"
                                      valueLabelDisplay="on"
                                      //getAriaValueText={field.label}
                                      onChange={(event, value) => {
                                      handleChangeSlider(
                                      event,
                                      value,
                                      'findComps',
                                      field.name,
                                      false,
                                      )
                                      }}
                                      onChangeCommitted={(
                                      event,
                                      value,
                                      ) => {
                                      handleChangeSlider(
                                      event,
                                      value,
                                      'findComps',
                                      field.name,
                                      true,
                                      )
                                      }}
                                      value={[
                                      preferences.findComps[
                                      field.name
                                      ].min,
                                      preferences.findComps[
                                      field.name
                                      ].max,
                                      ]}
                                      min={field.min}
                                      max={field.max}
                                      step={
                                      field.step ? field.step : 1
                                      }
                                      valueLabelFormat={
                                      field.valueLabelFormat
                                      ? field.valueLabelFormat
                                      : (x) => x
                                      }
                                      sx={{ my: '1rem' }}
                                      />
                                  </Grid>
                                )
                              } else {
                                return (
                                  <Grid
                                    size={12}
                                    style={{ marginBottom: 10 }}
                                  >
                                    <Autocomplete
                                      id="checkboxes-tags-demo"
                                      options={field.options}
                                      value={
                                        preferences.findComps[
                                          field.name
                                        ]
                                      }
                                      onChange={(event, newValue) => {
                                        handleChangeDropdown(
                                          event,
                                          newValue,
                                          'findComps',
                                          field.name,
                                        )
                                      }}
                                      disableClearable
                                      renderOption={(
                                        props,
                                        option,
                                        { selected },
                                      ) => (
                                        <li {...props}>
                                          <Checkbox
                                            icon={
                                              <CheckBoxOutlineBlank fontSize="small" />
                                            }
                                            checkedIcon={
                                              <CheckBox fontSize="small" />
                                            }
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                          />
                                          {option +
                                            ' ' +
                                            field.suffix}
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          variant="outlined"
                                          label={field.label}
                                          placeholder={field.label}
                                        />
                                      )}
                                    />
                                  </Grid>
                                )
                              }
                            })}
                          </Grid>
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        {/* <Typography variant='subtitle1'>Adjusting the sliders and dropdowns change the values below. The values below are an example of Comps for a Property with 4 Beds, 2 Baths, 2000 Sqft and built in 1965</Typography> */}
                        <Card>
                          <CardContent style={{ padding: 10 }}>
                            <Grid
                              container
                              direction="row"
                              spacing={1}
                              sx={{ mb: 0, alignItems: 'center' }}
                              >
                              <Grid size={12}>
                                <Typography
                                  color="primary"
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: -10,
                                  }}
                                >
                                  Example Comp
                                </Typography>
                              </Grid>
                              {map(
                                propertyDetailsFields,
                                (section) => (
                                  <Grid
                                    size={{
                                      xs: 12,
                                      sm: section.width,
                                    }}
                                  >
                                    <TextField
                                      variant="outlined"
                                      size="small"
                                      autoFocus={section.autoFocus}
                                      margin="dense"
                                      value={section.value || ''}
                                      // onChange={event => onChange(event, section.name)}
                                      // id="input-with-icon-textfield134"
                                      label={section.label}
                                      required={
                                        section.required === true ||
                                        section.required === 'true'
                                      }
                                      placeholder={
                                        section.placeholder
                                      }
                                      disabled
                                      type="number"
                                      min={0}
                                      fullWidth
                                    />
                                  </Grid>
                                ),
                              )}
                            </Grid>
                            <Grid
                              container
                              direction="row"
                              spacing={1}
                              sx={{ mb: 0, alignItems: 'center' }}
                              >
                              <Grid size={12}></Grid>
                              {map(otherFields, (section) => (
                                <Grid
                                  size={{ xs: 12, sm: section.width }}
                                >
                                  <Autocomplete
                                    id="checkboxes-tags-demo"
                                    options={section.options}
                                    value={section.value}
                                    // onChange={(event, newValue) => {
                                    //   section.setter(newValue)
                                    // }}
                                    disabled
                                    disableClearable
                                    renderOption={(
                                      props,
                                      option,
                                      { selected },
                                    ) => (
                                      <li {...props}>
                                        <Checkbox
                                          icon={
                                            <CheckBoxOutlineBlankIcon fontSize="small" />
                                          }
                                          checkedIcon={
                                            <CheckBoxIcon fontSize="small" />
                                          }
                                          style={{ marginRight: 8 }}
                                          checked={selected}
                                        />
                                        {option +
                                          ' ' +
                                          section.suffix}
                                      </li>
                                    )}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        label={section.label}
                                        placeholder={section.label}
                                      />
                                    )}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {tab === 'Strategy Page Layout' && (
                <Grid container>
                  <Grid size={12}>
                    <Typography
                      variant="h5"
                      style={{ paddingTop: 25 }}
                    >
                      Strategy Page Layout
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 25 }}
                  >
                    <Typography variant="subtitle1">
                      Change your data layout preferences. Rearrange
                      the order by dragging them.
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <div
                    //
                    >
                      {map(menuTitleStategy, (title) => {
                        const sortedCards = sortBy(title.cards, [
                          function (o) {
                            return findIndex(
                              preferences[title.name],
                              (value) => value === o.name,
                            )
                          },
                        ])
                        return (
                          <Accordion
                            key={title.name}
                            expanded={expanded === title.name}
                            onChange={handleChange(title.name)}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1bh-content"
                              id="panel1bh-header"
                            >
                              <Typography
                              //
                              >
                                {getStrategyName(
                                  title.label,
                                  props?.session?.me?.activeOrg
                                    ?.member,
                                )}
                              </Typography>
                            </AccordionSummary>
                            <Divider />
                            <AccordionDetails>
                              <SortableHelper>
                                <SortableList
                                  items={sortedCards.map((c) =>
                                    String(c.name),
                                  )}
                                  strategy="xy"
                                  onSortEnd={onSortEnd(
                                    map(
                                      title.cards,
                                      (card) => card.name,
                                    ),
                                    title.name,
                                  )}
                                >
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={1}
                                  >
                                    {sortedCards.map((card) => (
                                      <Grid
                                        size={{ sm: 4, xl: 3 }}
                                        key={card.name}
                                        sx={{ display: 'flex' }}
                                        >
                                        <SortableItem
                                          id={String(card.name)}
                                          style={{
                                            display: 'flex',
                                            width: '100%',
                                          }}
                                        >
                                          {card.card}
                                        </SortableItem>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </SortableList>
                              </SortableHelper>
                            </AccordionDetails>
                          </Accordion>
                        )
                      })}
                    </div>
                  </Grid>
                </Grid>
              )}
              {tab === 'Launchpad' && (
                <Grid container>
                  <Grid size={12}>
                    <Typography
                      variant="h5"
                      style={{ paddingTop: 25 }}
                    >
                      Launchpad
                    </Typography>
                  </Grid>
                  <Grid
                    size={12}
                    style={{ paddingTop: 10, paddingBottom: 25 }}
                  >
                    <Typography variant="subtitle1">
                      Customize the Launchpad action buttons to your
                      liking.
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <SortableHelper>
                      <SortableList
                        items={sortedLaunchPad.map((b) =>
                          String(b.title),
                        )}
                        strategy="xy"
                        onSortEnd={onSortEnd(
                          map(
                            launchPadButtons,
                            (button) => button.title,
                          ),
                          'launchPad',
                        )}
                      >
                        <Grid
                          container
                          direction="column"
                          spacing={2}
                        >
                          <Grid>
                            <Typography variant="h4">
                              Active
                            </Typography>
                          </Grid>
                          <Grid>
                            <Card variant="outlined">
                              <Box sx={[{
                                "& .MuiGrid-item": { borderRight: "1px solid #eeeff8", borderBottom: "1px solid #eeeff8", position: "relative" },
                                "& .MuiGrid-item:nth-last-of-type(-n+1)": { borderBottomWidth: 0 },
                                "& .MuiGrid-item:nth-of-type(2n)": { borderRightWidth: 1 },
                                "& .MuiGrid-item:nth-last-of-type(-n+3)": { borderBottomWidth: 0 },
                                "& .MuiGrid-item:nth-of-type(3n)": { borderRightWidth: 0 },
                                "& .MuiGrid-item:nth-last-of-type(-n+4)": { borderBottomWidth: 0 },
                                "& .MuiGrid-item:nth-of-type(4n)": { borderRightWidth: 0 },
                                "& > .btn": { display: "block", border: 0, minWidth: "100%" },
                              }, {
                                "& .MuiGrid-item:nth-last-of-type(-n+3)": { borderBottomWidth: 0 },
                                "& .MuiGrid-item:nth-of-type(2n)": { borderRightWidth: 1 },
                                "& .MuiGrid-item:nth-of-type(3n)": { borderRightWidth: 0 },
                              }]}>
                                <Grid
                                  container
                                  direction="row"
                                  spacing={1}
                                >
                                  {map(
                                    slice(sortedLaunchPad, 0, 6),
                                    (value) => (
                                      <Grid
                                        size={{ sm: 4 }}
                                        key={value.title}
                                        sx={{ display: 'flex' }}
                                        >
                                        <SortableItem
                                          id={String(value.title)}
                                          style={{
                                            display: 'flex',
                                            width: '100%',
                                          }}
                                        >
                                          <Button
                                            style={{
                                            textAlign: 'center',
                                            }}
                                            variant="text"
                                            sx={{
                                              backgroundColor: "transparent",
                                              color: "#f8f9ff",
                                              border: 0,
                                              "&:not(:disabled):hover": { boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)", backgroundColor: "#f8f9ff", color: "#3b3e66" },
                                              "&:not(:disabled).active, &:hover": { backgroundColor: "#f8f9ff", color: "#3b3e66" },
"html[data-color-mode=\"dark\"] &:not(:disabled):hover, html[data-color-mode=\"dark\"] &:not(:disabled).active, html[data-color-mode=\"dark\"] &:hover, html[data-color-mode=\"dark\"] &.active": { backgroundColor: "#2a2a2a !important", color: "rgba(255, 255, 255, 0.87) !important", boxShadow: "none" },
                                              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
                                              "&:hover": { transform: "translateY(0px)" },
                                              width: "100%",
                                              display: "block",
                                              borderRadius: 0,
                                              boxShadow: "none",
                                              py: "1.5rem",
                                              px: "0.25rem",
                                            }}
                                            >
                                            {value.icon}
                                            <Box sx={[{ fontWeight: 700 }, {
                                              fontSize: "1rem !important",
                                              color: "#070919",
                                              "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                            }]}>
                                              {value.title}
                                            </Box>
                                            <Box sx={[{ mb: '0.25rem', color: 'rgba(7, 9, 25, 0.5)', }, { fontSize: "1rem !important" }]}>
                                              {value.subtitle}
                                            </Box>
                                          </Button>
                                        </SortableItem>
                                      </Grid>
                                    ),
                                  )}
                                </Grid>
                              </Box>
                            </Card>
                          </Grid>
                          <Grid>
                            <Typography variant="h4">
                              Available
                            </Typography>
                          </Grid>
                          <Grid>
                            <Box sx={[{
                              "& .MuiGrid-item": { borderRight: "1px solid #eeeff8", borderBottom: "1px solid #eeeff8", position: "relative" },
                              "& .MuiGrid-item:nth-last-of-type(-n+1)": { borderBottomWidth: 0 },
                              "& .MuiGrid-item:nth-of-type(2n)": { borderRightWidth: 1 },
                              "& .MuiGrid-item:nth-last-of-type(-n+3)": { borderBottomWidth: 0 },
                              "& .MuiGrid-item:nth-of-type(3n)": { borderRightWidth: 0 },
                              "& .MuiGrid-item:nth-last-of-type(-n+4)": { borderBottomWidth: 0 },
                              "& .MuiGrid-item:nth-of-type(4n)": { borderRightWidth: 0 },
                              "& > .btn": { display: "block", border: 0, minWidth: "100%" },
                            }, {
                              "& .MuiGrid-item:nth-last-of-type(-n+3)": { borderBottomWidth: 0 },
                              "& .MuiGrid-item:nth-of-type(2n)": { borderRightWidth: 1 },
                              "& .MuiGrid-item:nth-of-type(3n)": { borderRightWidth: 0 },
                            }]}>
                              <Grid
                                container
                                direction="row"
                                spacing={1}
                              >
                                {map(
                                  slice(sortedLaunchPad, 6),
                                  (value) => (
                                    <Grid
                                      size={{ sm: 4 }}
                                      key={value.title}
                                      sx={{ display: 'flex' }}
                                      >
                                      <SortableItem
                                        id={String(value.title)}
                                        style={{
                                          display: 'flex',
                                          width: '100%',
                                        }}
                                      >
                                        <Button
                                          style={{
                                          textAlign: 'center',
                                          }}
                                          variant="text"
                                          sx={{
                                            backgroundColor: "transparent",
                                            color: "#f8f9ff",
                                            border: 0,
                                            "&:not(:disabled):hover": { boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)", backgroundColor: "#f8f9ff", color: "#3b3e66" },
                                            "&:not(:disabled).active, &:hover": { backgroundColor: "#f8f9ff", color: "#3b3e66" },
"html[data-color-mode=\"dark\"] &:not(:disabled):hover, html[data-color-mode=\"dark\"] &:not(:disabled).active, html[data-color-mode=\"dark\"] &:hover, html[data-color-mode=\"dark\"] &.active": { backgroundColor: "#2a2a2a !important", color: "rgba(255, 255, 255, 0.87) !important", boxShadow: "none" },
                                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
                                            "&:hover": { transform: "translateY(0px)" },
                                            width: "100%",
                                            display: "block",
                                            borderRadius: 0,
                                            boxShadow: "none",
                                            py: "1.5rem",
                                            px: "0.25rem",
                                          }}
                                          >
                                          {value.icon}
                                          <Box sx={[{ fontWeight: 700 }, {
                                            fontSize: "1rem !important",
                                            color: "#070919",
                                            "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
                                          }]}>
                                            {value.title}
                                          </Box>
                                          <Box sx={[{ mb: '0.25rem', color: 'rgba(7, 9, 25, 0.5)', }, { fontSize: "1rem !important" }]}>
                                            {value.subtitle}
                                          </Box>
                                        </Button>
                                      </SortableItem>
                                    </Grid>
                                  ),
                                )}
                                {map(
                                  [...Array(launchPadExtra)],
                                  () => (
                                    <Grid size={{ sm: 4 }} />
                                  ),
                                )}
                              </Grid>
                            </Box>
                          </Grid>
                        </Grid>
                      </SortableList>
                    </SortableHelper>
                  </Grid>
                </Grid>
              )}
            </Content>
          </Box>
        </Root>
      </Dialog>

      <Dialog open={openReset} onClose={() => setOpenReset(false)}>
        <DialogTitle>Reset Preferences</DialogTitle>
        <DialogContent>
          <FormControl
            component="fieldset"
            //
          >
            <RadioGroup
              aria-label="reset action"
              name="resetAll"
              //
              value={resetAll}
              onChange={(event) => setResetAll(event.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={'Reset all preferences to default settings'}
              />
              <FormControlLabel
                value="tab"
                control={<Radio />}
                label={`Reset preferences in ${tab} to default settings`}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReset(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReset}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {!urlAffiliate && <BuyBox isOpen={openBuyBox} />}
    </Box>
  )
}
export default withSession(OrganizationPreferencesComponent)
